import { z } from 'zod';
import { formatPhone } from '@web/shared/util/masks';
import { addDays, weekdayOf, zonedDate, zonedInstant } from '@web/shared/util/zoned-time';
import type { PersonRef, UnitRef } from '../customers/contract';
import type { LeadStatus } from '../leads/contract';
import type { AutoReplyKind, ConnectionStatus } from './channels-contract';
import type { ConsentStatus, ConversationConsent } from './consent-contract';
import type {
  ConversationDistribution,
  OutcomeOnResolve,
  QueueKind,
  ReasonOnResolve,
  SystemDetail,
} from './distribution-contract';
import type { ConversationSla, SlaFilter } from './sla-contract';

export type { ChannelConnection } from './channels-contract';

/**
 * The inbox contract (docs/specs/008-caixa-de-entrada/spec.md, §3 and §6): the shapes the
 * API answers, the zod schemas of what it accepts and the pure rules both sides share (the
 * 24-hour window, quick reply variables, templates, editing). The screens and the mocked API
 * (`src/mocks/conversations`) use these same functions.
 */

/* ------------------------------------------------------------------ constants */

export const CHANNEL_TYPES = ['whatsapp', 'instagram', 'email'] as const;
export type ChannelType = (typeof CHANNEL_TYPES)[number];

export const CHANNEL_CAPABILITIES = [
  'templates',
  'serviceWindow',
  'media',
  'reactions',
  'reply',
  'edit',
  'delete',
  /** Reads and answers the WhatsApp groups of the number (spec 024): QR code only. */
  'groups',
] as const;
export type ChannelCapability = (typeof CHANNEL_CAPABILITIES)[number];

export const CONVERSATION_STATUSES = ['open', 'pending', 'snoozed', 'resolved'] as const;
export type ConversationStatus = (typeof CONVERSATION_STATUSES)[number];

/** The statuses of the folders of active conversations (rule 14). */
export const ACTIVE_STATUSES = ['open', 'pending'] as const;
export type ActiveStatus = (typeof ACTIVE_STATUSES)[number];

export const CONVERSATION_PRIORITIES = ['none', 'low', 'medium', 'high', 'urgent'] as const;
export type ConversationPriority = (typeof CONVERSATION_PRIORITIES)[number];

export const CONVERSATION_FOLDERS = [
  'mine',
  'unassigned',
  'all',
  'mentions',
  'snoozed',
  'resolved',
  // Epic 3.2 (docs/specs/009-filas-e-distribuicao/spec.md, rules 28 and 29): the folder of a
  // queue (with `queueId`) and the conversations waiting for an assignee.
  'queue',
  'waiting',
  // Spec 024: the WhatsApp groups (QR code connections only).
  'groups',
  // Spec 023: the conversations the receptionist holds, out of `unassigned` and `waiting`.
  'triage',
] as const;
export type ConversationFolder = (typeof CONVERSATION_FOLDERS)[number];

/** A one-to-one conversation, or a WhatsApp group (spec 024). */
export type ConversationKind = 'direct' | 'group';

/** The filters of the Groups folder (spec 024, rule 28). */
export const GROUP_FILTERS = [
  'awaiting',
  'responsible',
  'following',
  'no-responsible',
  'archived',
  'with-demand',
] as const;
export type GroupFilter = (typeof GROUP_FILTERS)[number];

/** What of a group a member hears (rule 23). */
export const ALERT_MODES = ['directed', 'all', 'muted'] as const;
export type AlertMode = (typeof ALERT_MODES)[number];

/** The fixed folders, shown as chips before the queues' ones. */
export const FIXED_FOLDERS = [
  'mine',
  'unassigned',
  'all',
  'mentions',
  'snoozed',
  'resolved',
] as const satisfies readonly ConversationFolder[];

/** `sla`: the nearest SLA deadline first (spec 010, rule 27). */
export const CONVERSATION_SORTS = ['recent', 'waiting', 'sla'] as const;
export type ConversationSort = (typeof CONVERSATION_SORTS)[number];

export const MESSAGE_TYPES = [
  'text',
  'image',
  'audio',
  'video',
  'document',
  'location',
  'contact',
  'template',
  'system',
] as const;
export type MessageType = (typeof MESSAGE_TYPES)[number];

export const MESSAGE_STATUSES = ['sending', 'sent', 'delivered', 'read', 'failed'] as const;
export type MessageStatus = (typeof MESSAGE_STATUSES)[number];

export interface MessageTranscript {
  status: 'pending' | 'done' | 'failed';
  text: string | null;
}

export const SENDER_TYPES = ['contact', 'member', 'system', 'bot'] as const;
export type SenderType = (typeof SENDER_TYPES)[number];

export const ATTACHMENT_KINDS = ['image', 'audio', 'video', 'document'] as const;
export type AttachmentKind = (typeof ATTACHMENT_KINDS)[number];

export const SYSTEM_EVENTS = [
  'assigned',
  'unassigned',
  'resolved',
  'reopened',
  'snoozed',
  'pending',
  'linked',
  // Epic 3.2: the distribution's decisions and the transfers (spec 009, rules 21 and 25).
  'auto-assigned',
  'queued',
  'transferred',
  // Epic 3.3: an automatic reply and a change of consent (spec 010, rules 20 and 32).
  'auto-replied',
  'consent-changed',
  // Spec 015: a member started the conversation.
  'started',
  // Spec 024: what happened in a WhatsApp group.
  'group-monitored',
  'group-member-joined',
  'group-member-left',
  'group-member-removed',
  'group-member-promoted',
  'group-member-demoted',
  'group-changed',
  'group-left',
  // Spec 023: how a pass through the receptionist ended.
  'triaged',
] as const;
export type SystemEvent = (typeof SYSTEM_EVENTS)[number];

export const TEMPLATE_CATEGORIES = ['marketing', 'utility', 'authentication'] as const;
export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number];

export const TEMPLATE_STATUSES = ['approved', 'pending', 'rejected'] as const;
export type TemplateStatus = (typeof TEMPLATE_STATUSES)[number];

export const QUICK_REPLY_VISIBILITIES = ['personal', 'shared'] as const;
export type QuickReplyVisibility = (typeof QUICK_REPLY_VISIBILITIES)[number];

export const SNOOZE_PRESETS = ['1h', 'tomorrow', 'next-monday'] as const;
export type SnoozePreset = (typeof SNOOZE_PRESETS)[number];

/** `assigneeMembershipId=none`: the conversations without an assignee. */
export const NO_ASSIGNEE = 'none';

export const CONVERSATION_LIMITS = {
  textMax: 4096,
  captionMax: 1024,
  labelMax: 40,
  labelsMax: 20,
  attachmentsMax: 10,
  searchMin: 2,
  searchMax: 120,
  pageSize: 25,
  maxPageSize: 50,
  messagesPageSize: 10,
  messagesMaxPageSize: 100,
  /** Rule 25: own outbound text messages are edited up to 15 minutes later (WhatsApp's own)… */
  editWindowMinutes: 15,
  /** …and own outbound messages deleted for everyone up to two days later. */
  deleteWindowMinutes: 48 * 60,
  /** Rule 18: the customer service window of the channel. */
  serviceWindowHours: 24,
  shortcutMin: 2,
  shortcutMax: 32,
  quickReplyTitleMax: 80,
  templateVariableMax: 1024,
  templateLinkSuffixMax: 200,
  /** The provider's limit for a text header, its variable filled. */
  templateHeaderMax: 60,
  bulkMax: 100,
} as const;

/** Rule 26: what each kind of attachment accepts. */
export const ATTACHMENT_RULES: Record<AttachmentKind, { maxBytes: number; mimeTypes: string[] }> = {
  image: { maxBytes: 5 * 1024 * 1024, mimeTypes: ['image/jpeg', 'image/png', 'image/webp'] },
  audio: {
    maxBytes: 16 * 1024 * 1024,
    mimeTypes: ['audio/ogg', 'audio/mpeg', 'audio/mp4', 'audio/aac', 'audio/wav', 'audio/webm'],
  },
  video: { maxBytes: 16 * 1024 * 1024, mimeTypes: ['video/mp4', 'video/3gpp'] },
  document: { maxBytes: 100 * 1024 * 1024, mimeTypes: [] },
};

/** Codes of the errors of this contract (translated in `errors.api.<CODE>`). */
export const CONVERSATION_ERROR_CODES = [
  'CONVERSATION_NOT_FOUND',
  'MESSAGE_NOT_FOUND',
  'QUICK_REPLY_NOT_FOUND',
  'TEMPLATE_NOT_FOUND',
  'CONVERSATION_INVALID',
  'MESSAGE_INVALID',
  'ATTACHMENT_INVALID',
  'TEMPLATE_VARIABLES_INVALID',
  'QUICK_REPLY_INVALID',
  'SNOOZE_INVALID',
  'CONVERSATION_WINDOW_CLOSED',
  'CONVERSATION_REPLY_FORBIDDEN',
  'CONVERSATION_ASSIGNEE_INVALID',
  'CONVERSATION_ASSIGNEE_OUT_OF_REACH',
  'CHANNEL_CAPABILITY_MISSING',
  'MESSAGE_EDIT_EXPIRED',
  'MESSAGE_NOT_EDITABLE',
  'TEMPLATE_NOT_APPROVED',
  'QUICK_REPLY_SHORTCUT_TAKEN',
  // Spec 024.
  'CONVERSATION_IS_GROUP',
  'CONVERSATION_NOT_GROUP',
  'CHANNEL_GROUP_NOT_FOUND',
  'GROUP_MEMBER_NOT_FOUND',
  'CHANNEL_GROUPS_UNAVAILABLE',
  'CHANNEL_GROUPS_DISABLED',
  'CHANNEL_GROUP_LEFT',
  'CHANNEL_GROUP_MONITORED',
  'CHANNEL_GROUPS_OFFLINE',
  'CHANNEL_GROUPS_SLOW',
  'GROUP_RESPONSIBLES_INVALID',
  'GROUP_INPUT_INVALID',
  'PLAN_LIMIT_GROUPS_REACHED',
] as const;
export type ConversationErrorCode = (typeof CONVERSATION_ERROR_CODES)[number];

/* ------------------------------------------------------------------ shapes */

export interface ConnectionRef {
  id: string;
  channelType: ChannelType;
  name: string;
  address: string;
  capabilities: ChannelCapability[];
  /** Only a connected channel sends (spec 010, rule 6). */
  status: ConnectionStatus;
  /** Archived: it never sends again, the conversation is history (absent reads as no). */
  archived?: boolean;
}

export interface ContactIdentity {
  id: string;
  channelType: ChannelType;
  address: string;
  profileName: string | null;
  /** The contact's profile picture, when the provider shared one (WhatsApp QR, Instagram). */
  avatarUrl: string | null;
}

export interface ConversationContact {
  identity: ContactIdentity;
  /** The contact's name, the profile name, or the formatted address (unknown contact). */
  displayName: string;
  customer: { id: string; displayName: string } | null;
  contact: { id: string; name: string } | null;
}

export interface ConversationLabel {
  id: string;
  name: string;
}

export type MessageShareKind = 'reel' | 'post' | 'share';

export interface MessageShare {
  kind: MessageShareKind;
  /** The post on instagram.com; null when Instagram sent none. */
  url: string | null;
  caption: string | null;
}

export interface MessagePreview {
  id: string;
  direction: 'in' | 'out';
  type: MessageType;
  text: string | null;
  /** Who sent an outbound message (null for the contact). */
  senderName: string | null;
  senderMembershipId: string | null;
  /** Sent from the connected phone, outside the CRM. */
  sentOutside?: boolean;
  /** A call to the connected number (WhatsApp QR code). */
  call?: { video: boolean } | null;
  /** A shared Instagram reel, post or link (spec 058): what it is. */
  share?: { kind: MessageShareKind } | null;
  status: MessageStatus | null;
  deleted: boolean;
  createdAt: string;
}

/** Someone of the team who follows a group (spec 024, rule 20). */
export interface GroupWatcher {
  membershipId: string;
  name: string;
  role: 'responsible' | 'follower';
  /** The main responsible (D2): listed first. */
  primary: boolean;
  alertMode: AlertMode;
}

/** The WhatsApp group of a group conversation (spec 024). */
export interface ConversationGroupInfo {
  id: string;
  subject: string;
  description: string | null;
  size: number;
  businessIsAdmin: boolean;
  alertKeywords: string[];
  anyMessageWaits: boolean;
  /** The number is no longer in the group. */
  left: boolean;
}

export interface ConversationSummary {
  id: string;
  /** Spec 024: a WhatsApp group; `contact` is then the group itself. Absent reads as direct. */
  kind?: ConversationKind;
  group?: ConversationGroupInfo | null;
  /** The responsibles and followers of a group. */
  watchers?: GroupWatcher[];
  /** Messages that called the business the caller has not read (rule 28). */
  unreadMentions?: number;
  connection: ConnectionRef;
  contact: ConversationContact;
  status: ConversationStatus;
  snoozedUntil: string | null;
  assignee: PersonRef | null;
  team: { id: string; name: string } | null;
  /** The queue the conversation waits or is attended in (spec 009, rule 14). */
  queueId: string | null;
  /**
   * `outcomeOnResolve`: whether resolving asks for an outcome (QA-31); `reasonOnResolve`:
   * whether it asks for a contact reason (spec 046).
   */
  queue: {
    id: string;
    name: string;
    outcomeOnResolve: OutcomeOnResolve;
    reasonOnResolve: ReasonOnResolve;
    /** What the queue is for (spec 048). */
    kind: QueueKind;
  } | null;
  /**
   * The outcome of the latest resolve, while the conversation stays resolved (QA-31); the API
   * always answers it (absent reads as none).
   */
  outcome?: { id: string; label: string } | null;
  /** Why it waits in the queue, without an assignee (spec 009, rule 22). */
  distribution: ConversationDistribution | null;
  /** The receptionist holds it: no queue nor attendant yet (spec 023; absent reads as no). */
  inTriage?: boolean;
  /** The recepção that holds it, while it does. */
  reception?: { id: string; name: string } | null;
  unit: UnitRef;
  priority: ConversationPriority;
  labels: ConversationLabel[];
  lastMessage: MessagePreview | null;
  lastMessageAt: string;
  lastInboundAt: string | null;
  /** The first inbound message still without an answer (rule 28). */
  waitingSince: string | null;
  /** Inbound messages after the caller's last read (rule 27). */
  unreadCount: number;
  /** The caller was mentioned in a note here. */
  mentioned: boolean;
  /** The deadline of the next pending SLA target (spec 010, rule 25). */
  slaDueAt: string | null;
  /** The SLA of the current cycle; null without a policy (spec 010, rules 21–25). */
  sla: ConversationSla | null;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationAbilities {
  reply: boolean;
  note: boolean;
  assign: boolean;
  resolve: boolean;
  /** Spec 009, rule 23. */
  transfer: boolean;
}

/**
 * "Buscar mensagens anteriores no celular" (spec 032, rule 11): only a direct conversation of
 * a QR code number can ask; `state` is the last request (null: none yet).
 */
export interface EarlierMessages {
  available: boolean;
  state: 'pending' | 'loaded' | 'exhausted' | null;
  at: string | null;
}

export interface Conversation extends ConversationSummary {
  deals: { id: string; title: string }[];
  /** What the caller may do here (rules 13–17). */
  can: ConversationAbilities;
  /** The end of the service window (rule 18); null when the contact never wrote. */
  windowClosesAt: string | null;
  /** Spec 067: the contact's latest message from a click-to-WhatsApp ad (official WhatsApp). */
  adArrivedAt: string | null;
  /** Spec 067: until when Meta does not charge what is sent (its free entry point window). */
  freeWindowEndsAt: string | null;
  /** The consent of the contact's identity (spec 010, rule 35). */
  consent: ConversationConsent;
  earlierMessages: EarlierMessages;
}

export interface ConversationList {
  items: ConversationSummary[];
  nextCursor: string | null;
  total: number;
}

export interface ConversationCounts {
  folders: Record<
    Exclude<ConversationFolder, 'resolved' | 'queue' | 'groups' | 'triage'>,
    number
  > & {
    /** Active WhatsApp groups (spec 024). */
    groups?: number;
    /**
     * With the receptionist (spec 023): a number for who oversees the inbox while a flow is on;
     * null or absent hides the folder.
     */
    triage?: number | null;
  };
  unread: { mine: number; unassigned: number };
  /** The conversations in the folder of each of the caller's queues (spec 009, rule 30). */
  queues: Record<string, number>;
  /** The recepções that are on, with their conversations in triage (spec 023; empty: none). */
  receptions?: { id: string; name: string; count: number }[];
  /** Active visible conversations at risk or breached (spec 010, rule 27). */
  sla: { atRisk: number; breached: number };
}

/** The numbers of the home's service blocks (spec 044), `GET /conversations/summary`. */
export interface ConversationHomeSummary {
  /** Direct conversations assigned to me. */
  mine: { open: number; pending: number; waitingReply: number };
  /** Resolutions over the conversations I see: by me, and by anyone. */
  resolved: { mineToday: number; mineYesterday: number; allToday: number; allYesterday: number };
  /** The organization has at least one active SLA policy. */
  slaConfigured: boolean;
}

export interface Attachment {
  id: string;
  kind: AttachmentKind;
  name: string;
  mimeType: string;
  size: number;
  url: string;
  durationSeconds: number | null;
  voice: boolean;
}

export interface MessageReaction {
  emoji: string;
  reactor: 'contact' | 'member';
  membershipId: string | null;
  /** In a group: the member who reacted (spec 024). */
  identityId?: string | null;
  name: string;
}

/** Who wrote a group message (spec 024, rules 10 and 27). */
export interface MessageAuthor {
  identityId: string;
  /** Null: WhatsApp hides who it is ("Membro sem número"). */
  name: string | null;
  /** Null when WhatsApp hides the phone. */
  phone: string | null;
}

/** A contact the person shared in a message (spec 035): a vCard's name, phones and e-mails. */
export interface ContactCard {
  name: string;
  /** Digits: the WhatsApp id when the card had one, else the number as written. */
  phones: string[];
  emails: string[];
  organization: string | null;
}

/** Who a shared contact already is in the CRM; the card offers nothing when it is someone. */
export type SharedContactMatch =
  | { kind: 'customer'; id: string; name: string }
  | { kind: 'contact'; id: string; name: string }
  | { kind: 'lead'; id: string; name: string; status: LeadStatus };

export interface MessageContactCard extends ContactCard {
  match: SharedContactMatch | null;
}

export interface Message {
  id: string;
  conversationId: string;
  direction: 'in' | 'out';
  sender: { type: SenderType; membershipId: string | null; name: string };
  /** In a group: the member who wrote it. */
  author?: MessageAuthor | null;
  /** In a group: it called the business (rule 21). */
  directed?: boolean;
  /** In a group: the demand opened from it (spec 025, rule 6). */
  demand?: {
    taskId: string;
    open: boolean;
    assignee: { membershipId: string; name: string };
  } | null;
  private: boolean;
  type: MessageType;
  text: string | null;
  attachment: Attachment | null;
  /** The file when there is none to show yet: being fetched, or gone from the provider. */
  attachmentState?: 'pending' | 'unavailable' | null;
  /**
   * An audio's transcript (spec 051): queued, done (the text; empty = no speech recognized) or
   * failed. Null = never asked for.
   */
  transcript?: MessageTranscript | null;
  location: {
    latitude: number;
    longitude: number;
    name: string | null;
    address: string | null;
  } | null;
  /** The contacts the person shared (vCards); empty for any other message. */
  contactCards: ContactCard[];
  /** The contact called the connected number (WhatsApp QR code); the CRM cannot answer it. */
  call: { video: boolean } | null;
  /**
   * A reel, post or link shared on Instagram (spec 058): only its link and caption — the CRM
   * never downloads someone else's video. Null (or absent) for any other message.
   */
  share?: MessageShare | null;
  /**
   * An e-mail (spec 042): its subject and who else it went to; `hasHtml` = the original can be
   * opened ("Ver e-mail original"). Null (or absent) for any other message.
   */
  email?: MessageEmail | null;
  /** An attachment of an e-mail: the `email.messageId` of the e-mail it came in (spec 042). */
  emailPartOf?: string | null;
  template: { id: string; name: string; variables: string[] } | null;
  system: {
    event: SystemEvent;
    actor: PersonRef | null;
    target: PersonRef | null;
    /** What the distribution decided, or where a transfer went (spec 009). */
    detail: SystemDetail | null;
    /** An automatic reply (sent or skipped) or a change of consent (spec 010). */
    auto: SystemAuto | null;
  } | null;
  /** A message the system sent by itself (spec 010, rule 20). */
  autoReply: AutoReplyKind | null;
  /** Sent by the business outside the CRM: the phone's WhatsApp app, the Instagram app. */
  sentOutside: boolean;
  replyTo: { id: string; senderName: string; type: MessageType; text: string | null } | null;
  reactions: MessageReaction[];
  mentions: PersonRef[];
  status: MessageStatus | null;
  failureReason: string | null;
  editedAt: string | null;
  /** What it said before each edit by its author, oldest first (the contact sees the last). */
  previousVersions?: { text: string; replacedAt: string }[];
  deletedAt: string | null;
  createdAt: string;
}

export interface SystemAuto {
  kind: 'out-of-hours' | 'greeting' | 'consent';
  /** The reply could not go out (the window closed and no template, rule 19). */
  skipped: boolean;
  status: ConsentStatus | null;
  keyword: string | null;
}

export interface MessagePage {
  /** Oldest first. */
  items: Message[];
  /** The cursor of the older page, or null at the start of the conversation. */
  previousCursor: string | null;
}

export type UploadedAttachment = Attachment;

export interface TemplateLinkButton {
  type: 'url' | 'phone';
  text: string;
}

/** A link or phone button of a template, as the API reads it from the provider's components. */
export interface MessageTemplateLinkButton extends TemplateLinkButton {
  /** Position among all the buttons of the template. */
  index: number;
  /** The approved link (a dynamic one ends on `{{1}}`); null for a phone. */
  url: string | null;
  /** Whether the link takes its end on each send. */
  dynamic: boolean;
}

/**
 * The end of a dynamic link as typed: URL characters only (no spaces or accents), so it goes
 * out exactly as the preview shows it (the API's `LINK_SUFFIX_PATTERN`).
 */
export const LINK_SUFFIX_PATTERN = /^[A-Za-z0-9\-._~:/?#[\]@!$&'()*+,;=%]+$/;

export interface MessageTemplate {
  id: string;
  channelConnectionId: string;
  name: string;
  language: string;
  category: TemplateCategory;
  status: TemplateStatus;
  header: string | null;
  body: string;
  footer: string | null;
  /** The quick reply buttons. */
  buttons: string[];
  /**
   * The URL and phone buttons: the provider adds them to every send; a dynamic link takes the
   * end typed on the send.
   */
  linkButtons: MessageTemplateLinkButton[];
  variableCount: number;
  /** Why the provider refused it (spec 010, rule 11). */
  rejectionReason: 'invalid-format' | 'policy' | 'other' | null;
  /** One example per variable, sent with the request (rule 12). */
  examples: string[];
  /**
   * Whether the inbox can send it: false for what it does not support yet (a media header,
   * named variables), even when the provider approved it (spec 015, rule 12).
   */
  sendable: boolean;
  /** The text header takes `{{1}}` on each send (absent reads as no). */
  headerVariable?: boolean;
  /**
   * Whether a send that fills the header variable can send it (starting a conversation, the
   * inbox); `sendable` stays false for those, so the sends that fill nothing keep them out.
   * Absent reads as `sendable`.
   */
  sendableWithHeader?: boolean;
  /**
   * Whether the inbox can send it filling the end of its dynamic links and the header variable;
   * `sendable` stays false for those, so where no end is asked (starting a conversation) they
   * stay out.
   */
  sendableWithLinks: boolean;
  /** When it was asked from here; null for those that came from the provider. */
  submittedAt: string | null;
}

/** One page of a connection's templates (`GET /channel-connections/:id/templates`). */
export interface MessageTemplatePage {
  items: MessageTemplate[];
  /** Templates matching the search, across every page. */
  total: number;
  page: number;
  pageSize: number;
}

/** What the page asks: `q` searches the name and the body; `pageSize` up to 100. */
export interface MessageTemplatePageQuery {
  q?: string;
  page: number;
  pageSize: number;
}

export interface QuickReply {
  id: string;
  shortcut: string;
  title: string | null;
  body: string;
  visibility: QuickReplyVisibility;
  owner: PersonRef | null;
  createdAt: string;
  updatedAt: string;
}

export interface BulkConversationResult {
  updated: number;
  failed: { id: string; code: string }[];
}

/* ------------------------------------------------------------------ realtime */

/** The events of `GET /api/realtime` (spec §5), as the web reads them. */
export const conversationEventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('conversation.created'),
    data: z.object({ conversationId: z.string() }),
  }),
  z.object({
    type: z.literal('conversation.updated'),
    data: z.object({ conversationId: z.string() }),
  }),
  z.object({
    type: z.literal('message.created'),
    data: z.object({ conversationId: z.string(), messageId: z.string() }),
  }),
  z.object({
    type: z.literal('message.updated'),
    data: z.object({ conversationId: z.string(), messageId: z.string() }),
  }),
  z.object({
    type: z.literal('conversation.typing'),
    data: z.object({ conversationId: z.string(), typing: z.boolean() }),
  }),
  z.object({ type: z.literal('notification.created'), data: z.object({}) }),
  // Spec 010: a connection changed (status, health, templates) and a contact's consent.
  z.object({ type: z.literal('channel.updated'), data: z.object({ connectionId: z.string() }) }),
  z.object({
    type: z.literal('consent.updated'),
    data: z.object({ channelType: z.string(), address: z.string() }),
  }),
]);
export type ConversationEvent = z.infer<typeof conversationEventSchema>;

/* ------------------------------------------------------------------ schemas */

const id = z.string().min(1);

/** The value of a text header's `{{1}}`: the provider allows one, within the header's length. */
export const templateHeaderVariablesSchema = z
  .array(z.string().trim().min(1).max(CONVERSATION_LIMITS.templateHeaderMax))
  .max(1);
const messageText = z.string().trim().min(1).max(CONVERSATION_LIMITS.textMax);
export const EMAIL_SUBJECT_MAX = 200;
/** The subject of an answer by e-mail (spec 042); the other channels ignore it. */
const emailSubject = z.string().trim().max(EMAIL_SUBJECT_MAX).optional();

export interface MessageEmail {
  /** The `Message-ID` of an e-mail received (its attachments point at it); null for ours. */
  messageId?: string | null;
  subject: string | null;
  to: string[];
  cc: string[];
  hasHtml: boolean;
}

/** "Re: <subject>" for an answer, without piling up prefixes (the API does the same). */
export function replySubject(subject: string | null | undefined): string {
  const trimmed = subject?.trim() ?? '';
  if (trimmed === '') return '';
  return /^(?:re|res|resp)\s*:/i.test(trimmed) ? trimmed : `Re: ${trimmed}`;
}

export const sendMessageSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('text'),
      text: messageText,
      replyToId: id.optional(),
      // In a group: the members the text mentions as `@Name` (spec 024, rule 17).
      mentions: z.array(id).max(50).optional(),
      subject: emailSubject,
    })
    .strict(),
  z.object({ kind: z.literal('note'), text: messageText }).strict(),
  z
    .object({
      kind: z.literal('attachments'),
      attachmentIds: z.array(id).min(1).max(CONVERSATION_LIMITS.attachmentsMax),
      caption: z.string().trim().max(CONVERSATION_LIMITS.captionMax).optional(),
      replyToId: id.optional(),
      subject: emailSubject,
    })
    .strict(),
  z
    .object({
      kind: z.literal('template'),
      templateId: id,
      variables: z.array(z.string().trim().min(1).max(CONVERSATION_LIMITS.templateVariableMax)),
      /** The end of each dynamic link of the template, in the order of its buttons. */
      linkSuffixes: z
        .array(
          z
            .string()
            .trim()
            .min(1)
            .max(CONVERSATION_LIMITS.templateLinkSuffixMax)
            .regex(LINK_SUFFIX_PATTERN),
        )
        .max(2)
        .optional(),
      /** The value of the text header's `{{1}}`, when the template has one. */
      headerVariables: templateHeaderVariablesSchema.optional(),
    })
    .strict(),
]);
export type SendMessageInput = z.input<typeof sendMessageSchema>;

export const editMessageSchema = z.object({ text: messageText }).strict();
export type EditMessageInput = z.input<typeof editMessageSchema>;

export const reactionSchema = z.object({ emoji: z.string().min(1).max(16).nullable() }).strict();
export type ReactionInput = z.input<typeof reactionSchema>;

const labelNamesSchema = z
  .array(z.string().trim().min(1).max(CONVERSATION_LIMITS.labelMax))
  .max(CONVERSATION_LIMITS.labelsMax);

export const updateConversationSchema = z
  .object({
    priority: z.enum(CONVERSATION_PRIORITIES).optional(),
    labels: labelNamesSchema.optional(),
  })
  .strict();
export type UpdateConversationInput = z.input<typeof updateConversationSchema>;

export const assignConversationSchema = z
  .object({
    assigneeMembershipId: id.nullable(),
    teamId: id.nullable().optional(),
  })
  .strict();
export type AssignConversationInput = z.input<typeof assignConversationSchema>;

/**
 * `POST /conversations/:id/resolve`: the outcome and the contact reason are optional unless the
 * queue requires them (QA-31, spec 046).
 */
export const resolveConversationSchema = z
  .object({ outcomeId: id.nullable().optional(), reasonId: id.nullable().optional() })
  .strict();
export type ResolveConversationInput = z.input<typeof resolveConversationSchema>;

export const snoozeConversationSchema = z.object({ until: z.iso.datetime() }).strict();
export type SnoozeConversationInput = z.input<typeof snoozeConversationSchema>;

export const linkConversationSchema = z
  .object({ customerId: id, contactId: id.nullable() })
  .strict();
export type LinkConversationInput = z.input<typeof linkConversationSchema>;

export const linkDealSchema = z.object({ dealId: id }).strict();

const bulkIds = z.array(id).min(1).max(CONVERSATION_LIMITS.bulkMax);
export const bulkConversationSchema = z.discriminatedUnion('action', [
  z
    .object({
      ids: bulkIds,
      action: z.literal('resolve'),
      outcomeId: id.nullable().optional(),
      reasonId: id.nullable().optional(),
    })
    .strict(),
  z
    .object({ ids: bulkIds, action: z.literal('assign'), assigneeMembershipId: id.nullable() })
    .strict(),
  z
    .object({
      ids: bulkIds,
      action: z.literal('label'),
      label: z.string().trim().min(1).max(CONVERSATION_LIMITS.labelMax),
    })
    .strict(),
]);
export type BulkConversationInput = z.input<typeof bulkConversationSchema>;

export const SHORTCUT_PATTERN = /^[a-z0-9-]+$/;

const quickReplyFields = {
  shortcut: z
    .string()
    .trim()
    .toLowerCase()
    .min(CONVERSATION_LIMITS.shortcutMin)
    .max(CONVERSATION_LIMITS.shortcutMax)
    .regex(SHORTCUT_PATTERN),
  title: z.string().trim().max(CONVERSATION_LIMITS.quickReplyTitleMax).nullable().optional(),
  body: z.string().trim().min(1).max(CONVERSATION_LIMITS.textMax),
  visibility: z.enum(QUICK_REPLY_VISIBILITIES),
};

export const quickReplyInputSchema = z.object(quickReplyFields).strict();
export type QuickReplyInput = z.input<typeof quickReplyInputSchema>;

export const updateQuickReplySchema = z.object(quickReplyFields).partial().strict();
export type UpdateQuickReplyInput = z.input<typeof updateQuickReplySchema>;

/** Spec 065: the inbox filter of the 24-hour window. */
export const WINDOW_FILTERS = ['closing'] as const;
export type WindowFilter = (typeof WINDOW_FILTERS)[number];

const optionalEnum = <T extends readonly [string, ...string[]]>(values: T) =>
  z.enum(values).optional();

export const conversationListQuerySchema = z.object({
  folder: optionalEnum(CONVERSATION_FOLDERS),
  status: optionalEnum(ACTIVE_STATUSES),
  channelConnectionId: id.optional(),
  labelId: id.optional(),
  assigneeMembershipId: id.optional(),
  teamId: id.optional(),
  queueId: id.optional(),
  receptionId: id.optional(),
  priority: optionalEnum(CONVERSATION_PRIORITIES),
  unread: z.enum(['true', 'false']).optional(),
  q: z
    .string()
    .trim()
    .min(CONVERSATION_LIMITS.searchMin)
    .max(CONVERSATION_LIMITS.searchMax)
    .optional(),
  customerId: id.optional(),
  contactId: id.optional(),
  connected: z.enum(['true', 'false']).optional(),
  sort: optionalEnum(CONVERSATION_SORTS),
  sla: z.enum(['at_risk', 'breached']).optional(),
  window: z.enum(WINDOW_FILTERS).optional(),
  cursor: id.optional(),
  limit: z.coerce.number().int().min(1).max(CONVERSATION_LIMITS.maxPageSize).optional(),
});

export interface ConversationListQuery {
  folder?: ConversationFolder;
  status?: ActiveStatus;
  channelConnectionId?: string;
  labelId?: string;
  assigneeMembershipId?: string;
  teamId?: string;
  queueId?: string;
  /** The `triage` folder of one recepção (spec 023). */
  receptionId?: string;
  priority?: ConversationPriority;
  unread?: boolean;
  q?: string;
  customerId?: string;
  /** The direct conversations of one person, in every channel (spec 064). */
  contactId?: string;
  /** Only the conversations whose channel is connected: the ones that can still send. */
  connected?: boolean;
  sort?: ConversationSort;
  /** Spec 010, rule 27. */
  sla?: SlaFilter;
  /** Spec 065, R7: the contact waits and the 24-hour window closes within two hours. */
  window?: WindowFilter;
  /** In the Groups folder (spec 024, rule 28). */
  groupFilter?: GroupFilter;
  /** Spec 048: the queues of one kind, plus the conversations without a queue. */
  area?: QueueKind;
  cursor?: string;
  limit?: number;
}

/** One resolve of a conversation — an "atendimento" closed (spec 064). */
export interface ConversationResolution {
  id: string;
  resolvedAt: string;
  outcome: { id: string; label: string } | null;
  resolvedBy: PersonRef | null;
}

/** `GET /conversations/:id/resolutions`, most recent first. */
export interface ConversationResolutionList {
  items: ConversationResolution[];
}

export const messagesQuerySchema = z.object({
  before: id.optional(),
  limit: z.coerce.number().int().min(1).max(CONVERSATION_LIMITS.messagesMaxPageSize).optional(),
});

/* ------------------------------------------------------------------ rules */

export interface ServiceWindow {
  open: boolean;
  /** When the window closes (or closed); null when the contact never wrote. */
  closesAt: string | null;
  remainingMs: number;
}

/** Rule 18: open for 24 hours since the last inbound message. */
export function serviceWindow(lastInboundAt: string | null, now: Date = new Date()): ServiceWindow {
  if (!lastInboundAt) return { open: false, closesAt: null, remainingMs: 0 };
  const closes =
    new Date(lastInboundAt).getTime() + CONVERSATION_LIMITS.serviceWindowHours * 3_600_000;
  const remainingMs = Math.max(0, closes - now.getTime());
  return { open: remainingMs > 0, closesAt: new Date(closes).toISOString(), remainingMs };
}

/**
 * Spec 067, the free window of a contact who came from a click-to-WhatsApp ad:
 * - `open`: Meta opened it — everything sent until `endsAt` is free, templates included;
 * - `answer`: the contact came from an ad and the window is not open yet — answering before
 *   `by` (the end of the service window of that message) opens it;
 * - null: nothing to say.
 */
export type AdFreeWindow = { kind: 'open'; endsAt: string } | { kind: 'answer'; by: string };

export function adFreeWindow(
  conversation: Pick<Conversation, 'adArrivedAt' | 'freeWindowEndsAt'>,
  now: Date = new Date(),
): AdFreeWindow | null {
  const { adArrivedAt, freeWindowEndsAt } = conversation;
  if (freeWindowEndsAt && new Date(freeWindowEndsAt).getTime() > now.getTime()) {
    return { kind: 'open', endsAt: freeWindowEndsAt };
  }
  if (!adArrivedAt) return null;
  const window = serviceWindow(adArrivedAt, now);
  return window.open && window.closesAt ? { kind: 'answer', by: window.closesAt } : null;
}

/** Whether free text (not a template) can go out on the connection now (rule 18). */
export function canSendFreeText(
  connection: { capabilities: readonly ChannelCapability[] },
  lastInboundAt: string | null,
  now: Date = new Date(),
): boolean {
  if (!connection.capabilities.includes('serviceWindow')) return true;
  return serviceWindow(lastInboundAt, now).open;
}

/** A person can still answer on Instagram for 7 days, with Meta's `HUMAN_AGENT` tag. */
export function canReplyAsHumanAgent(
  connection: { channelType: string },
  lastInboundAt: string | null,
  now: Date = new Date(),
): boolean {
  if (connection.channelType !== 'instagram' || !lastInboundAt) return false;
  return new Date(lastInboundAt).getTime() + 7 * 24 * 3_600_000 > now.getTime();
}

/** The variables of quick replies (rule 30), as written in the text. */
export const QUICK_REPLY_VARIABLES = [
  'contato.nome',
  'cliente.nome',
  'atendente.nome',
  /** The protocol of the newest ticket in work of the conversation (specs 046 and 054). */
  'ticket.protocolo',
] as const;
/** `atendimento.protocolo` is the name the variable had before the ticket: it keeps working. */
export type QuickReplyVariable = (typeof QUICK_REPLY_VARIABLES)[number] | 'atendimento.protocolo';

/**
 * The text of a quick reply with its variables replaced (rule 30). A variable without a
 * value stays in the text, for whoever sends it to complete.
 */
/**
 * A name a message can call someone by, or null. A contact registered without a profile name
 * is named after its phone, and WhatsApp hides a profile it does not share behind dots
 * ("+55∙∙∙∙∙∙∙∙34"): neither has a letter, and neither is a way to greet a person.
 */
export function personName(name: string | null | undefined): string | null {
  const trimmed = name?.trim() ?? '';
  return /\p{L}/u.test(trimmed) ? trimmed : null;
}

export function renderQuickReply(
  body: string,
  values: Partial<Record<QuickReplyVariable, string | null>>,
): string {
  return body.replace(/\{\{\s*([a-z.]+)\s*\}\}/g, (whole, name: string) => {
    const value = values[name as QuickReplyVariable];
    return value ? value : whole;
  });
}

/** The quick replies matching what follows the `/` (shortcut first, then title). */
export function matchQuickReplies<T extends { shortcut: string; title: string | null }>(
  replies: readonly T[],
  query: string,
): T[] {
  const search = query.trim().toLowerCase();
  if (search === '') return replies.toSorted((a, b) => a.shortcut.localeCompare(b.shortcut));
  const starts = replies.filter((reply) => reply.shortcut.startsWith(search));
  const rest = replies.filter(
    (reply) =>
      !starts.includes(reply) &&
      (reply.shortcut.includes(search) || (reply.title ?? '').toLowerCase().includes(search)),
  );
  const byShortcut = (a: T, b: T) => a.shortcut.localeCompare(b.shortcut);
  return [...starts.toSorted(byShortcut), ...rest.toSorted(byShortcut)];
}

/** The `/query` typed at the start of the composer, or null. */
export function quickReplyQuery(text: string): string | null {
  const match = /^\/([a-z0-9-]*)$/i.exec(text);
  return match ? (match[1] ?? '') : null;
}

/** How many positional variables (`{{1}}`, `{{2}}`…) a template body has (rule 19). */
export function templateVariableCount(body: string): number {
  let max = 0;
  for (const match of body.matchAll(/\{\{(\d+)\}\}/g)) {
    max = Math.max(max, Number(match[1]));
  }
  return max;
}

/** The template body with its variables filled; missing ones keep the placeholder. */
export function renderTemplate(body: string, variables: readonly string[]): string {
  return body.replace(/\{\{(\d+)\}\}/g, (whole, index: string) => {
    const value = variables[Number(index) - 1];
    return value && value.trim() !== '' ? value : whole;
  });
}

/** The kind of attachment a file becomes (rule 26). */
export function attachmentKindOf(mimeType: string): AttachmentKind {
  if (ATTACHMENT_RULES.image.mimeTypes.includes(mimeType)) return 'image';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (ATTACHMENT_RULES.video.mimeTypes.includes(mimeType)) return 'video';
  return 'document';
}

/** Why a file cannot be attached, or null (rule 26). */
export function attachmentProblem(file: {
  type: string;
  size: number;
}): 'too-large' | 'empty' | null {
  if (file.size === 0) return 'empty';
  const kind = attachmentKindOf(file.type);
  return file.size > ATTACHMENT_RULES[kind].maxBytes ? 'too-large' : null;
}

/** What has text to edit: a text, or the caption of a photo, video or document. */
const EDITABLE_MESSAGE_TYPES: ReadonlySet<string> = new Set(['text', 'image', 'video', 'document']);

/**
 * Rule 25: an own message, not failed nor deleted — a text (or a caption) edited within 15 minutes, any
 * message deleted within two days (an own note, anytime).
 */
export function canChangeMessage(
  message: {
    direction: 'in' | 'out';
    private: boolean;
    type: MessageType;
    status: MessageStatus | null;
    deletedAt: string | null;
    createdAt: string;
    senderMembershipId: string | null;
  },
  membershipId: string,
  change: 'edit' | 'delete' = 'edit',
  now: Date = new Date(),
): boolean {
  if (message.deletedAt || message.senderMembershipId !== membershipId) return false;
  if (message.private) return message.type === 'text';
  if (message.direction !== 'out' || message.status === 'failed') return false;
  // A text, or the caption of a photo, video or document.
  if (change === 'edit' && !EDITABLE_MESSAGE_TYPES.has(message.type)) return false;
  const age = now.getTime() - new Date(message.createdAt).getTime();
  const minutes =
    change === 'delete'
      ? CONVERSATION_LIMITS.deleteWindowMinutes
      : CONVERSATION_LIMITS.editWindowMinutes;
  return age <= minutes * 60_000;
}

const SNOOZE_TIME = '09:00';

/** The instant of a snooze preset (rule 8), in the organization's zone. */
export function snoozeUntil(
  preset: SnoozePreset,
  timeZone: string,
  now: Date = new Date(),
): string {
  const today = zonedDate(now, timeZone);
  switch (preset) {
    case '1h':
      return new Date(now.getTime() + 3_600_000).toISOString();
    case 'tomorrow':
      return zonedInstant(addDays(today, 1), SNOOZE_TIME, timeZone).toISOString();
    case 'next-monday': {
      const ahead = (8 - weekdayOf(today)) % 7 || 7;
      return zonedInstant(addDays(today, ahead), SNOOZE_TIME, timeZone).toISOString();
    }
  }
}

/**
 * Brazilian mobiles may arrive from WhatsApp without the ninth digit (`55 73 9856-9414`).
 * Display always shows the canonical form with the 9 (`55 73 99856-9414`), matching the
 * contact phone and the identity `addressKey`.
 */
function canonicalWhatsAppAddress(address: string): string {
  if (address.startsWith('55') && address.length === 12 && /^[6-9]/.test(address.slice(4))) {
    return `${address.slice(0, 4)}9${address.slice(4)}`;
  }
  return address;
}

/** A phone address (digits with the country code) as people read it. */
export function formatAddress(channelType: ChannelType, address: string): string {
  // The API may already send the username with its @.
  if (channelType === 'instagram') return `@${address.replace(/^@/, '')}`;
  if (channelType !== 'whatsapp') return address;
  const digits = canonicalWhatsAppAddress(address);
  if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) {
    return `+55 ${formatPhone(digits.slice(2))}`;
  }
  return `+${digits}`;
}

/**
 * The address shown under a contact's name, or null when the name already is that address — a
 * contact without a name is called by its number, which should not show twice.
 */
export function addressUnlessName(
  channelType: ChannelType,
  address: string,
  name: string,
): string | null {
  const shown = formatAddress(channelType, address);
  const digits = (text: string) => text.replace(/\D/g, '');
  if (shown === name) return null;
  return digits(shown) !== '' && digits(shown) === digits(name) ? null : shown;
}

/**
 * The national digits (DDD + number) of a Brazilian WhatsApp address, to match contacts and
 * prefill the phone of a new customer — canonical, so a mobile that arrived without the
 * ninth digit gets it back (`557398698815` → `73998698815`).
 */
export function nationalDigits(address: string): string {
  const digits = canonicalWhatsAppAddress(address);
  return digits.startsWith('55') && digits.length >= 12 ? digits.slice(2) : digits;
}

/* ------------------------------------------------------------ scheduled messages */

/** What a scheduled message sends: a text, or an approved template. */
export type ScheduledMessageContent =
  | { kind: 'text'; text: string }
  | {
      kind: 'template';
      templateId: string;
      variables: string[];
      linkSuffixes?: string[];
      headerVariables?: string[];
    };

/**
 * A message to send at `scheduledAt` (spec 008, agendamento), as its author. The list shows
 * the pending ones and the ones that failed (`failure` is an error code).
 */
export interface ScheduledMessage {
  id: string;
  conversationId: string;
  message: ScheduledMessageContent;
  scheduledAt: string;
  status: 'scheduled' | 'sending' | 'failed';
  failure: string | null;
  author: { membershipId: string; name: string };
  createdAt: string;
}

export interface ScheduleMessageInput {
  message: ScheduledMessageContent;
  scheduledAt: string;
}

/** The earliest a message may be scheduled for: a minute from now. */
export const SCHEDULE_MIN_LEAD_MS = 60_000;
